import { useCallback, useEffect, useRef, useState } from 'react';
import { Wrap, WrapItem, Tag, TagLabel, TagCloseButton, VStack, TagRightIcon, useDisclosure } from '@chakra-ui/react';
import { OnChangeValue, OptionProps } from 'react-select';
import { RiAttachment2 } from 'react-icons/ri';
import { AutoComplete, ISelectOptions } from '../AutoComplete';
import { SelectInput } from '../Select';
import { AlertUploadFile } from './Alert/AlertUploadFile';

type ChipsProps = {
  creatable?: boolean;
  label?: string;
  name: string;
  options: ISelectOptions[];
  autoFocus?: boolean;
  uploadFile?: boolean;
  placeholder?: string;
  fields: ChipsValues[];
  onInputChange?: (inputValue, actionMeta) => void;
  optionRender?: (props: OptionProps<ISelectOptions>) => JSX.Element;
  append: (value: ChipsValues) => void;
  update: (index: number, value: ChipsValues) => void;
  remove: (id: number) => void;
  viewTooltip?: boolean;
  tooltipLabel?: string;
};

export type ChipsValues = {
  id?: string;
  label: string;
  value: string;
  file?: File;
  detalhes?: object;
  descricaoMinima?: string;
};

function Chips({ creatable, label, name, options, autoFocus, uploadFile, placeholder, fields, append, update, remove, onInputChange, optionRender, viewTooltip, tooltipLabel }: ChipsProps) {
  const autoCompleteRef = useRef(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectOptions, setSelectOptions] = useState<ISelectOptions[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleUploadFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0 && fileRef.current) {
        update(fields.length - 1, { ...fields[fields.length - 1], file: files[0] });
        fileRef.current.value = '';
      }
    },
    [fields, update],
  );

  const handleAlertConfirm = useCallback(() => {
    if (uploadFile && fileRef.current) {
      fileRef.current.click();
    }
  }, [uploadFile]);

  const handleChange = useCallback(
    (newValue: OnChangeValue<ISelectOptions, false>) => {
      if (newValue && !fields.find(item => item.value === newValue.value)) {
        append({
          label: newValue.label,
          value: newValue.value,
          detalhes: newValue.detalhes,
          descricaoMinima: newValue.descricaoMinima,
        });

        if (uploadFile && fileRef.current) {
          onOpen();
        }

        const autoComplete = autoCompleteRef?.current;
        if (autoComplete) {
          const { firstChild } = autoComplete as { firstChild: HTMLDivElement };
          if (firstChild) firstChild.textContent = placeholder || 'Escolha...';
        }
      }
    },
    [append, fields, onOpen, placeholder, uploadFile],
  );

  const handleRemove = useCallback(
    (idChip: number) => {
      remove(idChip);
    },
    [remove],
  );

  useEffect(() => {
    setSelectOptions(options.filter(option => !fields.find(selectedOption => selectedOption.value === option.value)));
  }, [fields, options]);

  return (
    <VStack w="100%" align="flex-start" spacing="0">
      <AlertUploadFile isOpen={isOpen} onClose={onClose} onConfirm={handleAlertConfirm} />
      <input type="file" id={`file-${name}`} ref={fileRef} onChange={handleUploadFile} style={{ display: 'none' }} />
      {creatable ? (
        <AutoComplete
          label={label}
          placeholder={placeholder}
          ref={autoCompleteRef}
          colorScheme="onsupply"
          name={name}
          options={selectOptions}
          showBorder
          isClearable
          autoFocus={autoFocus}
          onChange={handleChange}
          onInputChange={onInputChange}
          viewTooltip={viewTooltip}
          tooltipLabel={tooltipLabel}
        />
      ) : (
        <SelectInput
          label={label}
          placeholder={placeholder}
          ref={autoCompleteRef}
          colorScheme="onsupply"
          name={name}
          options={selectOptions}
          showBorder
          isClearable
          autoFocus={autoFocus}
          onChange={handleChange}
          onInputChange={onInputChange}
          optionRender={optionRender}
          viewTooltip={viewTooltip}
          tooltipLabel={tooltipLabel}
        />
      )}
      <Wrap pt="2">
        {fields.map((chip, i) => (
          <WrapItem key={chip.id}>
            <Tag size="lg" borderRadius="full" variant="solid" bg="#EBEBEB" color="gray.700">
              <TagLabel>{chip.descricaoMinima != null ? chip.descricaoMinima : chip.label}</TagLabel>
              {chip.file && <TagRightIcon as={RiAttachment2} />}
              <TagCloseButton bg="#7B7B7B" opacity="1" color="white" onClick={() => handleRemove(i)} />
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
    </VStack>
  );
}

export { Chips };
